import React, { useState, useRef, useCallback, useEffect } from "react";

// ─────────────────────────────────────────────────────────────
// NEO Guided Photo Capture v2
// Customer selects Make + Model → the app maps it to a body
// type (coupe, sedan, suv, pickup, minivan) → every silhouette
// overlay in the photo flow matches their actual vehicle shape.
//
// onComplete({ vehicle, photos }) fires when finished.
//   vehicle = { make, model, bodyType }
//   photos  = [{ position, label, file, dataUrl }, ...]
// ─────────────────────────────────────────────────────────────

// ── Inline icons (replaces lucide-react) ───────────────────
const IconSvg = ({ size = 24, children, style }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={style}>
    {children}
  </svg>
);
const CameraIcon       = (p) => <IconSvg {...p}><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" /><circle cx="12" cy="13" r="4" /></IconSvg>;
const CheckIcon        = (p) => <IconSvg {...p}><polyline points="20 6 9 17 4 12" /></IconSvg>;
const RotateCcwIcon    = (p) => <IconSvg {...p}><polyline points="1 4 1 10 7 10" /><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" /></IconSvg>;
const ChevronLeftIcon  = (p) => <IconSvg {...p}><polyline points="15 18 9 12 15 6" /></IconSvg>;
const PlusIcon         = (p) => <IconSvg {...p}><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></IconSvg>;
const SparklesIcon     = (p) => <IconSvg {...p}><path d="M12 2l2.5 5.5L20 10l-5.5 2.5L12 18l-2.5-5.5L4 10l5.5-2.5z" /></IconSvg>;
const XIcon            = (p) => <IconSvg {...p}><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></IconSvg>;
const CarIcon          = (p) => <IconSvg {...p}><path d="M5 17h14M5 13l1.5-4.5A2 2 0 0 1 8.4 7h7.2a2 2 0 0 1 1.9 1.5L19 13" /><path d="M5 13h14v4H5z" /><circle cx="7.5" cy="17.5" r="1.5" /><circle cx="16.5" cy="17.5" r="1.5" /></IconSvg>;

// ── Vehicle data: make → model → body type ─────────────────
const VEHICLES = {
  Toyota: { Camry: "sedan", Corolla: "sedan", Avalon: "sedan", "GR86": "coupe", Supra: "coupe", RAV4: "suv", Highlander: "suv", "4Runner": "suv", Sequoia: "suv", Tacoma: "pickup", Tundra: "pickup", Sienna: "minivan" },
  Honda: { Accord: "sedan", Civic: "sedan", "Civic Coupe": "coupe", "CR-V": "suv", Pilot: "suv", Passport: "suv", "HR-V": "suv", Ridgeline: "pickup", Odyssey: "minivan" },
  Ford: { Fusion: "sedan", Mustang: "coupe", Escape: "suv", Explorer: "suv", Expedition: "suv", Edge: "suv", Bronco: "suv", "F-150": "pickup", "F-250": "pickup", Ranger: "pickup", Maverick: "pickup" },
  Chevrolet: { Malibu: "sedan", Camaro: "coupe", Corvette: "coupe", Equinox: "suv", Traverse: "suv", Tahoe: "suv", Suburban: "suv", Blazer: "suv", Silverado: "pickup", "Silverado HD": "pickup", Colorado: "pickup" },
  Ram: { "1500": "pickup", "2500": "pickup", "3500": "pickup" },
  GMC: { Terrain: "suv", Acadia: "suv", Yukon: "suv", Sierra: "pickup", Canyon: "pickup" },
  Jeep: { Cherokee: "suv", "Grand Cherokee": "suv", Wrangler: "suv", Compass: "suv", Wagoneer: "suv", Gladiator: "pickup" },
  Nissan: { Altima: "sedan", Sentra: "sedan", Maxima: "sedan", "Z": "coupe", Rogue: "suv", Murano: "suv", Pathfinder: "suv", Armada: "suv", Frontier: "pickup", Titan: "pickup" },
  Hyundai: { Elantra: "sedan", Sonata: "sedan", Tucson: "suv", "Santa Fe": "suv", Palisade: "suv", Kona: "suv", "Santa Cruz": "pickup" },
  Kia: { Forte: "sedan", K5: "sedan", Stinger: "sedan", Sportage: "suv", Sorento: "suv", Telluride: "suv", Soul: "suv", Carnival: "minivan" },
  Subaru: { Legacy: "sedan", Impreza: "sedan", WRX: "sedan", BRZ: "coupe", Outback: "suv", Forester: "suv", Crosstrek: "suv", Ascent: "suv" },
  Dodge: { Charger: "sedan", Challenger: "coupe", Durango: "suv", "Grand Caravan": "minivan" },
  Chrysler: { "300": "sedan", Pacifica: "minivan", Voyager: "minivan" },
  Tesla: { "Model 3": "sedan", "Model S": "sedan", "Model Y": "suv", "Model X": "suv", Cybertruck: "pickup" },
  Volkswagen: { Jetta: "sedan", Passat: "sedan", Tiguan: "suv", Atlas: "suv", Taos: "suv" },
  BMW: { "3 Series": "sedan", "5 Series": "sedan", "4 Series Coupe": "coupe", X3: "suv", X5: "suv", X7: "suv" },
  "Mercedes-Benz": { "C-Class": "sedan", "E-Class": "sedan", "S-Class": "sedan", GLC: "suv", GLE: "suv", GLS: "suv", Sprinter: "minivan", Metris: "minivan" },
  Lexus: { ES: "sedan", IS: "sedan", RC: "coupe", RX: "suv", NX: "suv", GX: "suv" },
  Mazda: { Mazda3: "sedan", Mazda6: "sedan", "MX-5 Miata": "coupe", "CX-5": "suv", "CX-9": "suv", "CX-50": "suv" },
  Audi: { A4: "sedan", A6: "sedan", A5: "coupe", Q5: "suv", Q7: "suv" },
};

const BODY_TYPES = [
  { id: "coupe", label: "Coupe" },
  { id: "sedan", label: "Sedan" },
  { id: "suv", label: "SUV" },
  { id: "pickup", label: "Pickup Truck" },
  { id: "minivan", label: "Mini Van" },
];

// ── Ghost silhouettes: filled modern profiles per body type ──
// Visually verified renders. Vehicles face left; flip for passenger side.
// Style: filled body with window cutouts (evenodd) + wheel rings,
// drawn in currentColor so the parent sets the tint.

const Ghost = ({ children }) => (
  <svg viewBox="0 0 320 130" className="gpc-svg-fill">
    {children}
  </svg>
);

const Wheel = ({ cx, cy, r }) => (
  <circle cx={cx} cy={cy} r={r - 6} fill="none" stroke="currentColor" strokeWidth="5" />
);

// ── Side silhouettes: exterior-outline-only, traced from Larry's art ──
// Single clean contour per body type, rendered as a stroked line.
const SIDE_ART = {
  coupe: {
    viewBox: "0 0 888.3 282",
    transform: "",
    paths: [
      "M503.7 0c-2.7 1.8-4.7 2-18 2s-15.3.2-18 2c-2.4 1.6-4.7 2-12 2q-9.1.1-11.5 1.5c-1.4.8-4.7 1.4-7.5 1.5q-5 0-8 2t-8.1 2q-5.3 0-7.5 1.9a37 37 0 0 1-14.8 6.1c-1.4 0-4 .9-5.6 2s-4.3 2-6 2c-1.6 0-4.3.9-6 2a13 13 0 0 1-6.2 2c-2.3 0-4.5 1.1-7.8 4-3.4 2.9-5.5 4-8 4a11 11 0 0 0-5.6 1.8c-7.2 5.5-43.7 22.2-48.6 22.2a12 12 0 0 0-8.3 2.5C293 65.8 292 66 281.8 66c-9.4 0-11.6.3-14.1 2-2.7 1.8-4.7 2-20 2s-17.3.2-20 2c-2.6 1.7-4.7 2-16 2s-13.4.3-16 2-4.7 2-14 2-11.5.3-14 2c-2.6 1.7-4.7 2-16 2s-13.4.3-16 2c-2.4 1.6-4.7 2-12 2s-9.6.4-12 2c-2.3 1.5-4.7 2-10 2-4.2.1-8 .7-9.5 1.5-1.4.8-4.8 1.4-7.6 1.5q-5.2 0-7.5 1.9a37 37 0 0 1-15 6.1c-1.4 0-3.7.8-5 1.8s-7.3 4.2-13.4 7.2c-6 2.9-12.3 6.5-13.8 7.9a34 34 0 0 1-7 4.6 12 12 0 0 0-6.2 6.1 40 40 0 0 1-5.3 7.8c-1.7 2-3.2 4.2-3.2 4.9S7 144.1 5.7 146a13 13 0 0 0-2.5 6.4 15 15 0 0 1-1.5 5.6c-2.5 4.4-2.1 40.6.5 44.5 1.5 2.2 2 4.7 2 9.6 0 12.1 13.4 30.8 22.2 30.9 1.8.1 4.4.7 5.8 1.5 2 1.2 10.2 1.4 45.9 1.5h43.4l12.2 12c6.7 6.6 13 12 13.8 12s3.9 2 6.7 4.5c3.7 3.3 5.9 4.5 8.3 4.5 1.8.1 4.3.7 5.7 1.5 1.9 1.1 6.4 1.4 18 1.4s16-.3 18-1.4a15 15 0 0 1 5.6-1.5c1.6 0 4.5-1.1 6.4-2.5s3.9-2.5 4.6-2.5 3.4-1.8 5.9-4 5.3-4 6-4c2.5 0 10.3-7.9 11.5-11.5 3-9.3-16.4-8.5 200-8.5s196.9-.8 200 8.4c1.2 3.6 16.8 19.6 19.2 19.6.8 0 2.9 1.1 4.8 2.5s4.8 2.5 6.4 2.5c1.7.1 4.2.7 5.6 1.5 1.9 1.1 6.9 1.4 21.5 1.5 19 0 19 0 33-6.9q22.4-11.3 25.5-20.7c2.6-7.9 10-12.4 20.2-12.4 7.6 0 9.8-.4 12.3-2s4.7-2 14-2c7.6-.1 11.8-.5 13.5-1.5 1.4-.8 4.8-1.4 7.5-1.5q5 0 8-2c1.7-1.1 4.4-2 6-2 1.7 0 4.4-.9 6-2 1.7-1.1 4.5-2 6.2-2 2.4 0 4.4-1 7.4-3.7 2.3-2.1 6-4.7 8.3-5.8 3.5-1.7 4.7-3.2 8-10 2.3-4.4 4.9-9.1 5.9-10.5s1.7-4.3 1.7-7.5c0-2.7.7-6.1 1.5-7.5 2.3-4.1 2-20.7-.5-24.5-1.5-2.2-2-4.7-2-9.5 0-8.7-10.5-30.6-19-39.5-6-6.3-7.2-15.6-3-22 5.4-8.2-2.5-23.1-15.8-29.8-5.2-2.6-6-2.7-22-2.7-15 0-17-.2-19.7-2-2.5-1.7-4.7-2-14.1-2-10.2 0-11.3-.2-14.4-2.5A13 13 0 0 0 770 45c-3 0-18.6-7-22.7-10.2-1.3-1-3.8-1.8-5.5-1.8s-4.4-.9-6.1-2-4.3-2-6-2c-1.6 0-4.3-.9-6-2-1.6-1.1-4.3-2-6-2-1.6 0-4.3-.9-6-2-1.6-1.1-4.3-2-6-2-1.6 0-4.3-.9-6-2-1.6-1.1-4.3-2-6-2-1.6 0-4.3-.9-6-2-1.6-1.1-4.3-2-6-2a15 15 0 0 1-5.5-1.5 25 25 0 0 0-9.6-1.5c-6 0-7.6-.4-10.4-2.5a13 13 0 0 0-6.4-2.5 15 15 0 0 1-5.6-1.5q-2.5-1.3-11.5-1.5c-7.3 0-9.6-.4-12-2-4.7-3.1-112.3-3.1-117 0",
    ],
  },
  sedan: {
    viewBox: "0 0 952.3 309.9",
    transform: "",
    paths: [
      "M539.6 0c-2.7 1.8-4.7 2-18 2s-15.3.2-18 2c-2.6 1.7-4.7 2-16 2-9.4.1-13.7.5-15.5 1.5-1.4.8-4.7 1.4-7.5 1.5q-5 0-8 2t-8 2-8 2-8 2-8 2c-1.6 1.1-4.3 2-6 2-1.6 0-4.3.9-6 2-1.6 1.1-4.3 2-6 2-1.6 0-4.3.9-6 2a13 13 0 0 1-6.1 2c-1.7 0-4.2.8-5.5 1.9a37 37 0 0 1-15 6.1c-1.4 0-3.7.8-5 1.8s-9.1 5.1-17.4 9.2a131 131 0 0 0-17.7 9.8A54 54 0 0 1 301.2 70c-9 0-11.1.3-13.6 2-2.7 1.8-4.7 2-22 2s-19.3.2-22 2c-2.6 1.7-4.7 2-16 2s-13.4.3-16 2-4.7 2-14 2-11.5.3-14 2c-2.3 1.5-4.7 2-10 2s-7.7.5-10 2c-2.4 1.6-4.7 2-12 2s-9.6.4-12 2c-2.3 1.5-4.7 2-10 2s-7.7.5-10 2-4.7 2-10 2-7.7.5-10 2c-2.4 1.6-4.7 2-11.6 2-7.7 0-9.2.3-15.6 3.5a35 35 0 0 1-10.1 3.5c-1.6 0-4 .8-5.3 1.8s-5.8 3.5-10 5.5c-8 3.8-35 29.3-35 33 0 .9-2 3.9-4.4 6.7-3.3 3.7-4.5 5.9-4.5 8.3a15 15 0 0 1-1.5 5.7c-2.1 3.7-2.1 52.3 0 56 .8 1.4 1.4 4.7 1.5 7.5q0 5 2 8c1 1.6 2 4.5 2 6.4 0 7.3 13 18.1 22 18.1 5.8 0 8.2.5 10.5 2 2.9 1.9 4.8 2 46.3 2 47.3 0 49.2.4 53.9 12 2.3 5.5 7 9.8 15.3 13.7 4 2 8.6 4.5 9.9 5.5s3.8 1.8 5.5 1.8c1.7.1 4.2.7 5.6 1.5 1.5.8 5.3 1.4 9.5 1.5 5.3 0 7.7.5 10 2 3.8 2.5 9.2 2.5 13 0 2.2-1.5 4.7-2 9.6-2 11.5 0 22.9-6.6 39.4-22.9 13.2-13.1 13.2-13.1 20.6-13.1a26 26 0 0 0 9.9-1.5c2-1.2 31-1.4 202.3-1.5 199.7 0 199.7 0 204.2 4.1 2.4 2.2 4.9 5.6 5.5 7.4 1.2 3.6 9 11.5 11.4 11.5.8 0 3.6 1.8 6 4 2.6 2.2 5.3 4 6 4s2.7 1.1 4.6 2.5 4.8 2.5 6.4 2.5c1.7.1 4.2.7 5.6 1.5 3.6 2.1 36.4 2.1 40 0a15 15 0 0 1 5.6-1.5c1.7 0 4.2-.8 5.5-1.8a88 88 0 0 1 9.4-5.2c8.2-3.8 21.8-16.4 23.6-21.7 1.9-5.9 6-8.3 14.2-8.3 5.5 0 7.9-.5 10.2-2 2.4-1.6 4.7-2 12-2s9.6-.4 12-2 4.7-2 12-2q9-.2 11.5-1.5a15 15 0 0 1 5.5-1.5c1.7 0 4.4-.9 6-2 1.7-1.1 4.4-2 6-2 1.7 0 4.6-1.1 6.5-2.5s3.9-2.5 4.6-2.5 3.4-1.8 5.9-4 5.5-4 6.6-4 2.2-.8 2.5-1.8a93 93 0 0 1 7.6-15.1q1.8-2.3 1.8-7.5c0-2.8.7-6.2 1.5-7.6 2.5-4.4 2-48.6-.5-52.5-1.5-2.3-2-4.7-2-10s-.5-7.7-2-10c-2-2.8-2-4.7-2-30 0-32 1-29.4-13.2-36.4C920.6 62 920.6 62 904 62c-14.8 0-16.8-.2-19.5-2-2.3-1.5-4.7-2-10.1-2-6 0-7.5-.4-10.4-2.5a13 13 0 0 0-6.4-2.5c-1.7 0-4.4-.9-6.1-2s-4.3-2-6-2c-1.6 0-4.3-.9-6-2-1.6-1.1-4.3-2-6-2-1.6 0-4.3-.9-6-2-1.6-1.1-4.2-2-5.8-2-3 0-18.5-7-22.6-10.2q-2.4-1.8-7.5-1.8t-8.1-2c-1.6-1.1-4.3-2-6-2-1.6 0-4.3-.9-6-2-1.6-1.1-4.3-2-6-2-1.6 0-4.3-.9-6-2q-3-2-8-2t-8-2-8-2-8-2-8-2c-2.7-.1-6.1-.7-7.5-1.5q-2.5-1.3-11.5-1.5c-7.3 0-9.6-.4-12-2-2.7-1.8-4.7-2-22-2s-19.3-.2-22-2c-4.7-3.1-100.3-3.1-105 0",
    ],
  },
  suv: {
    viewBox: "0 0 964.7 360.1",
    transform: "",
    paths: [
      "M491.7 2c-2.7 1.8-4.7 2-20 2s-17.3.2-20 2c-2.3 1.5-4.7 2-10 2-4.2 0-8 .7-9.5 1.5-1.4.8-3.8 1.4-5.5 1.5-1.6 0-4.3.9-6 2-1.6 1-4.3 2-6 2-1.6 0-4.3.9-6 2a13 13 0 0 1-6.1 2c-1.7 0-4.2.8-5.5 1.8s-10.9 6-21.4 11.2a199 199 0 0 0-22.8 12.7c-2 1.8-4.4 3.3-5.1 3.3-.8 0-3.6 1.8-6.1 4s-5.2 4-6 4-3.5 1.8-6 4-5.3 4-6.1 4a30 30 0 0 0-7.9 6c-3.5 3.3-7.2 6-8 6a23 23 0 0 0-7.3 5.4 55 55 0 0 1-28 13.6c-3.6 0-5.7.6-8.2 2.5-3.3 2.4-4 2.5-18.4 2.5-13.4 0-15.4.2-18.1 2-2.6 1.7-4.7 2-16 2s-13.4.3-16 2c-2.7 1.8-4.7 2-18 2-11.1 0-15.6.4-17.5 1.5-1.4.8-4.8 1.4-7.6 1.5-3.7 0-5.9.6-8.4 2.5-3 2.2-4.4 2.5-12.4 2.5-7.4 0-9.7.4-12.1 2s-4.7 2-12 2q-9 0-11.5 1.5c-1.4.8-4.7 1.4-7.5 1.5q-5 0-8 2a13 13 0 0 1-6.1 2c-1.7 0-4.2.8-5.5 1.9a38 38 0 0 1-15 6q-2.7 0-7.9 4.6a20 20 0 0 1-6.7 4.5c-2.3 0-21.3 18.9-23 23a46 46 0 0 1-3.4 5.9c-1 1.3-1.9 3.8-1.9 5.5s-.9 4.4-2 6q-2 3.1-2 8c-.1 2.8-.7 6.2-1.5 7.6-.8 1.5-1.4 5.3-1.5 9.5 0 5.3-.5 7.7-2 10-2.6 3.9-3 56-.5 60.5.8 1.4 1.4 4.9 1.5 7.8 0 6 8.7 16.8 16 19.9a46 46 0 0 1 5.9 3.4c1.3 1 3.7 1.9 5.3 1.9s4.1.6 5.6 1.4c2.1 1 11.2 1.5 39.7 1.9 36.9.5 37 .5 42.6 3s9.9 6.6 9.9 9.4c0 2.3 11.9 14 15.5 15.3 1.8.6 4.3 2.2 5.5 3.5a32 32 0 0 0 20.9 8.5c4.9 0 7.4.5 9.6 2a43 43 0 0 0 24.5.5c1.4-.8 4.8-1.4 7.5-1.5q5 0 8-2c1.7-1.1 4.4-2 6.1-2s4.5-1.1 6.4-2.5 4-2.5 4.8-2.5 7.7-6.1 15.2-13.5c12.2-12 14.1-13.5 17.1-13.5 1.9 0 4.8-1 6.4-2 4.4-3 412.4-3.2 416.5-.3 1.3 1 4.3 2 6.5 2.3 3.3.4 4.3 1 5.7 4 3.1 6.6 26 28 29.9 28 1.9 0 4.8.9 6.4 2q3 2 8 2c2.8 0 6.1.7 7.5 1.5 1.8 1 6.1 1.4 16 1.4s14.2-.4 16-1.4c1.4-.8 4.8-1.4 7.5-1.5q5 0 8-2c1.7-1.1 4.4-2 6.2-2 2.4 0 4.5-1.2 8.3-4.5a20 20 0 0 1 6.7-4.5 73 73 0 0 0 19.3-19.5c1.3-4 9-7.5 16.1-7.5a12 12 0 0 0 7.9-2.5c3-2.2 4.4-2.5 12.4-2.5q9.1-.2 11.6-1.5a15 15 0 0 1 5.5-1.5c1.7 0 4.4-1 6-2q3-2 8-2t8-2c1.7-1.1 4.4-2 6.1-2s4.2-.8 5.5-1.8a90 90 0 0 1 15.2-7.6q1.5-.6 1.7-2.5c0-1.1 1.8-4.1 4-6.6s4-5.2 4-6c0-.6 1.1-2.6 2.5-4.5 1.4-2 2.5-4.8 2.5-6.4a15 15 0 0 1 1.5-5.6c3.7-6.5 1.9-44.1-2.5-52.5-4-7.6-4-7.6-4-34.3 0-25-.1-27-2-29.7-1.5-2.3-2-4.7-2-10-.1-4.2-.7-8-1.5-9.5a15 15 0 0 1-1.5-5.6c0-1.6-1.1-4.5-2.5-6.4s-2.5-4-2.5-4.7-2.7-4.3-6-7.8-6-7.2-6-8.1c0-1-4.5-6.2-10-12-10-10.2-10-10.2-10-27.1 0-22.4-4.7-33.2-14.4-33.3a15 15 0 0 1-5.6-1.5c-1.5-.8-5.3-1.4-9.5-1.5-5.3 0-7.7-.5-10-2-2.7-1.8-4.7-2-20-2s-17.3-.2-20-2c-2.8-1.8-4.7-2-28-2s-25.2-.2-28-2c-2.9-2-4.8-2-48-2s-45.1-.1-48-2c-2.9-2-4.9-2-94.5-2s-91.6 0-94.5 2",
    ],
  },
  minivan: {
    viewBox: "0 0 1048 372.1",
    transform: "",
    paths: [
      "M607.5 2c-3 1.9-4.8 2-74 2s-71.1 0-74 2c-2.8 1.8-4.7 2-24 2s-21.2.2-24 2c-2.6 1.7-4.7 2-16 2-9.4 0-13.7.5-15.5 1.5-1.4.8-4.7 1.4-7.5 1.5q-5 0-8 2t-8 2-8 2c-1.6 1-4.3 2-6 2-1.6 0-4.3.9-6 2a13 13 0 0 1-6.1 2c-1.7 0-4.2.8-5.5 1.8s-13 7-26 13.6A245 245 0 0 0 273.4 56c-1 1-5.2 3.6-9.4 5.6a61 61 0 0 0-13.2 8.9 23 23 0 0 1-7.3 5.4c-1 0-6.3 4.5-12 10s-11 10-12 10-5.2 3.4-9.5 7.5c-6.6 6.3-8.4 7.5-11.2 7.5-2.1 0-4.8 1-6.8 2.5-3.1 2.3-4.3 2.5-14.4 2.5q-11.4 0-13.6 1.5c-1.4.8-4.8 1.4-7.6 1.5-3.7 0-6 .6-8.4 2.5-3.1 2.3-4.3 2.5-14.4 2.5q-11.4 0-13.6 1.5a15 15 0 0 1-5.6 1.5c-1.6 0-4.5 1-6.4 2.5-2.8 2-4.5 2.5-10.4 2.5a25 25 0 0 0-9.6 1.5c-1.4.8-3.8 1.4-5.5 1.5-1.6 0-4.3.9-6 2-1.6 1-4.3 2-6 2-1.6 0-4.3.9-6 2a13 13 0 0 1-6.1 2c-1.6 0-4.5 1-6.4 2.5a13 13 0 0 1-4.6 2.5c-.7 0-3.4 1.8-6 4-2.4 2.2-5.2 4-6 4-3.3 0-13.4 11.2-17.1 19-2 4-4.5 8.6-5.5 9.9s-1.8 3.8-1.8 5.5-1 4.4-2 6c-1.1 1.7-2 4.4-2 6a15 15 0 0 1-1.5 5.6c-1.1 1.9-1.4 6.7-1.5 19.5 0 15.2-.2 17.3-2 20-1.7 2.6-2 4.7-2 14.5s.3 11.9 2 14.5 2 4.7 2 13.7c0 10.4 0 10.8 3.9 18.5a130 130 0 0 1 4.5 9.5c.3 1 1.4 1.8 2.4 1.8s3.3 1 5.2 2.5c1.9 1.4 4.8 2.5 6.4 2.5s4.2.7 5.6 1.5c2 1.2 10.4 1.4 47.7 1.5 50.4 0 49.4-.2 52.2 8.3 4.3 13 32.7 31.7 48 31.7 6.9 0 9.2.4 11.6 2a56 56 0 0 0 28.5.5 15 15 0 0 1 5.6-1.5c1.6 0 4.5-1.1 6.4-2.5s3.9-2.5 4.6-2.5 3.4-1.8 5.9-4 5.3-4 6-4c2.5 0 14.3-12 15.5-15.5 3.6-10.8 8.8-12.5 38.2-12.5 21.8-.1 27.8-.4 29.8-1.5 4.6-2.6 412.5-2.1 416.5.5q3 2 8 2c6.9 0 8.6 1.5 13.8 12 3.6 7.4 4.8 8.8 8.3 10.5a51 51 0 0 1 9.7 7.2c3 2.9 6.2 5.3 6.9 5.3s2.9 1 4.8 2.5c2.5 1.9 4.7 2.5 8.4 2.5 2.8 0 6.2.7 7.6 1.5 3.6 2 36.4 2 40 0a15 15 0 0 1 5.6-1.5c1.7 0 4.2-.8 5.5-1.8s7.6-4.4 14-7.5c11.8-5.8 22.9-15.1 22.9-19.2 0-3 5.5-7.5 9.2-7.5 2 0 4.8-1 6.8-2.5 3.3-2.4 4-2.5 18.4-2.5 13.4 0 15.4-.2 18-2 2.7-1.7 4.8-2 16-2 9.5-.1 13.8-.5 15.6-1.5 1.4-.8 4.8-1.4 7.5-1.5q5 0 8-2c1.7-1.1 4.4-2 6-2 1.7 0 4.6-1.1 6.5-2.5s4-2.5 4.8-2.5c2.8 0 17-15.1 20.2-21.3 2.6-5.3 3-7.1 3-14.6 0-7 .4-9.2 2-11.6 1.8-2.7 2-4.7 2-18.7 0-17.4-2-21.8-11-25.6-5.1-2.1-5-.6-5-42.7-.1-32-.3-39.5-1.5-41.5-.8-1.4-1.4-4.8-1.5-7.5q0-5-2-8t-2-8.1q0-5.2-2-7.5a37 37 0 0 1-6-15c0-1.4-.8-3.7-1.8-5s-3.5-5.8-5.5-10c-2-4.1-4.6-8.4-5.8-9.4-3.3-3-7-12.1-7-17.4q0-4.6-2-7.6c-1-1.7-2-4.4-2-6 0-1.7-.8-4.4-2-6-1-1.7-2-4.4-2-6.1 0-12-9.2-14.4-52.8-14.4-34.8 0-36.7-.1-39.6-2-3-2-5-2-138-2s-135 0-138-2c-1.6-1.1-4.6-2-6.5-2a14 14 0 0 0-6.5 2",
    ],
  },
  pickup: {
    viewBox: "0 0 1256.6 369.9",
    transform: "",
    paths: [
      "M563.5 0c-2.8 1.8-4.7 2-26 2s-23.2.2-26 2c-2.4 1.6-4.7 2-12 2s-9.6.4-12 2c-2.2 1.5-4.7 2-9.6 2-5.4 0-7.7.6-13.6 3.5a27 27 0 0 1-12 3.5 14 14 0 0 0-7.8 2 13 13 0 0 1-6.1 2c-1.7 0-4.2.8-5.5 1.8s-9.1 5.1-17.4 9.2a146 146 0 0 0-24 14c-.7 0-3.5 1.8-6 4s-5.2 4-6 4-3.5 1.8-6 4-5.3 4-6.2 4c-.8 0-7.1 5.4-13.8 12s-13 12-14 12c-.8 0-3.2 1.5-5.2 3.4A33 33 0 0 1 318 97c-1.5 0-4.2 1.1-6.1 2.5-3.4 2.5-3.4 2.5-34.4 2.5-29.4 0-31.3.1-34.1 2s-4.8 2-38 2-35.2.1-38 2-4.7 2-24 2-21.2.2-24 2c-2.7 1.8-4.7 2-22 2s-19.3.2-22 2c-2.4 1.6-4.7 2-12 2q-9.1.1-11.5 1.5c-1.4.8-4.8 1.4-7.6 1.5-3.7 0-6 .6-8.4 2.5-2 1.4-4 2.5-4.8 2.5-2.6 0-13.2 11.2-16.2 17.3-3 5.9-3 6.4-3 20.6 0 12.9-.2 15-2 17.6-1.5 2.3-2 4.7-2 10s-.5 7.7-2 10c-1.8 2.7-2 4.7-2 18s-.2 15.3-2 18C.4 240 0 242.2 0 250s.4 10 2 12.5c1.7 2.6 2 4.7 2 16 0 9.4.5 13.7 1.5 15.5a15 15 0 0 1 1.5 5.6c0 4.7 3.3 8 11.8 12.1 4.2 2 8.8 4.5 10 5.5 1.4 1 3.9 1.8 5.6 1.8 1.7.1 4.2.7 5.6 1.5 2 1.2 9.6 1.4 41.5 1.5 44.3 0 40-.8 44.8 9a59 59 0 0 0 25.2 25c3.9 1.9 8 4.2 9.4 5.2q2.3 1.8 7.5 1.8c2.8.1 6.2.7 7.6 1.5 2 1.1 7.2 1.4 24 1.4s22-.3 24-1.4a15 15 0 0 1 5.6-1.5c1.7 0 4.2-.8 5.5-1.8a88 88 0 0 1 9.4-5.2c8.2-3.9 25.7-20.2 27.6-25.7 2.5-7.8 4.3-8.3 30.2-8.3 18.2-.1 23.7-.4 25.7-1.5 4.6-2.6 552.5-2.1 556.5.5 1.7 1.1 4.5 2 6.3 2 4 0 15.5 10.3 17.2 15.5 1.2 3.7 9 11.5 11.5 11.5.9 0 4.7 2.9 8.5 6.5 5.6 5.3 7.5 6.5 10.2 6.5 1.8 0 4.7.9 6.3 2q3 2 8 2c2.8.1 6 .7 7.5 1.5 1.9 1.1 6.9 1.4 22 1.4s20-.3 22-1.4a15 15 0 0 1 5.6-1.5c1.7 0 4.2-.8 5.5-1.8s7.6-4.4 13.9-7.5a58 58 0 0 0 29.4-27.9c4-8.4 6.4-10.8 11-10.8 1.8 0 4.7-1.1 6.6-2.5 3.4-2.5 3.6-2.5 24.4-2.5 19.4 0 21.3-.2 24-2 2.7-1.7 4.8-2 16-2 11.4 0 13.5-.3 16-2 2.6-1.7 4.8-2 14-2 9.4 0 11.6-.3 14-2s4.8-2 12-2q9.1-.2 11.6-1.5a15 15 0 0 1 5.5-1.5c1.7 0 4.4-.9 6-2 1.7-1.1 4.4-2 6-2 1.7 0 4.6-1.1 6.5-2.5s4-2.5 4.8-2.5c2.6 0 13.2-11.2 16.2-17.3 6.7-13.4 3.6-41.1-5.5-49.7a16 16 0 0 1-3.5-5.6c-1.5-4.5-6.3-8.4-10.3-8.4-1.8 0-4.5-.9-6.2-2q-3-2-8.3-2c-10.3 0-11.2-2.1-11.2-25 0-24 1-26 14.5-26 13 0 17.5-5.6 17.5-21.9 0-14.2-1.8-17.4-13.2-23.1-8-3.9-345.4-4.9-351.3-1-5 3.2-51 3-55-.2a17 17 0 0 0-6.8-2.8c-5.8-.7-9.5-7.5-9.7-17.5 0-4.1-.7-8.1-1.5-9.5s-1.4-4.8-1.5-7.5q0-5-2-8c-1.1-1.7-2-4.4-2-6 0-1.7-1-4.4-2-6q-2-3-2-8.1t-1.8-7.5c-1-1.3-3.5-5.8-5.5-9.9-9.5-19.8-16.8-23-53.5-23-27 0-29-.1-31.7-2-4.7-3.1-132.3-3.1-137 0",
    ],
  },
};

const SideSilhouette = ({ bodyType = "sedan", flip }) => {
  const art = SIDE_ART[bodyType] || SIDE_ART.sedan;
  return (
    <svg
      viewBox={art.viewBox}
      className="gpc-svg-fill"
      style={flip ? { transform: "scaleX(-1)" } : undefined}
      preserveAspectRatio="xMidYMid meet"
    >
      <g transform={art.transform} fill="none" stroke="currentColor" strokeWidth="3" strokeLinejoin="round" opacity="0.95">
        {art.paths.map((d, i) => (
          <path key={i} d={d} vectorEffect="non-scaling-stroke" />
        ))}
      </g>
    </svg>
  );
};

// Front/rear: parametric per body type (roof height, width, beltline, wheel size)
const FR = {
  coupe: { roof: 38, width: 198, shoulder: 0.6, wheelR: 13 },
  sedan: { roof: 32, width: 192, shoulder: 0.58, wheelR: 13 },
  suv: { roof: 16, width: 194, shoulder: 0.48, wheelR: 15 },
  pickup: { roof: 12, width: 202, shoulder: 0.46, wheelR: 16 },
  minivan: { roof: 14, width: 198, shoulder: 0.5, wheelR: 14 },
};

const frBody = (p, rear) => {
  const x0 = 160 - p.width / 2, x1 = 160 + p.width / 2;
  const roofY = p.roof, shY = roofY + (104 - roofY) * p.shoulder;
  const wr = p.wheelR + 4;
  const c = rear ? 8 : 10; // shoulder curve weight
  return { x0, x1, roofY, shY, path: `M${x0} 104 L${x0} ${shY} C${x0} ${shY - 14 - (rear ? 0 : 2)} ${x0 + c} ${roofY + 11} ${x0 + 28} ${roofY + 5.5} L${x0 + 47} ${roofY + 1} C${x0 + 55} ${roofY} ${x1 - 55} ${roofY} ${x1 - 47} ${roofY + 1} L${x1 - 28} ${roofY + 5.5} C${x1 - c} ${roofY + 11} ${x1} ${shY - 14 - (rear ? 0 : 2)} ${x1} ${shY} L${x1} 104 L${x1 - 16} 104 A${wr} ${wr} 0 0 0 ${x1 - 16 - 2 * wr} 104 L${x0 + 16 + 2 * wr} 104 A${wr} ${wr} 0 0 0 ${x0 + 16} 104 L${x0} 104 Z` };
};

const FrontSilhouette = ({ bodyType = "sedan" }) => {
  const p = FR[bodyType] || FR.sedan;
  const { x0, x1, roofY, shY, path } = frBody(p, false);
  return (
    <Ghost>
      <g fill="currentColor">
        <path
          d={`${path} M${x0 + 44} ${roofY + 9} L${x1 - 44} ${roofY + 9} L${x1 - 32} ${shY - 6} L${x0 + 32} ${shY - 6} Z`}
          fillRule="evenodd" opacity="0.85"
        />
        {/* mirrors */}
        <path d={`M${x0 - 8} ${shY - 10} L${x0 + 2} ${shY - 14} L${x0 + 2} ${shY - 2} L${x0 - 8} ${shY - 4} Z`} opacity="0.85" />
        <path d={`M${x1 + 8} ${shY - 10} L${x1 - 2} ${shY - 14} L${x1 - 2} ${shY - 2} L${x1 + 8} ${shY - 4} Z`} opacity="0.85" />
      </g>
      {/* headlights */}
      <rect x={x0 + 12} y={shY + 8} width="42" height="13" rx="6.5" fill="none" stroke="currentColor" strokeWidth="3" />
      <rect x={x1 - 54} y={shY + 8} width="42" height="13" rx="6.5" fill="none" stroke="currentColor" strokeWidth="3" />
    </Ghost>
  );
};

const RearSilhouette = ({ bodyType = "sedan" }) => {
  const p = FR[bodyType] || FR.sedan;
  const { x0, x1, roofY, shY, path } = frBody(p, true);
  const truck = bodyType === "pickup";
  const glass = truck
    ? `M${x0 + 10} ${shY + 2} L${x1 - 10} ${shY + 2} L${x1 - 10} ${shY + 8} L${x0 + 10} ${shY + 8} Z`
    : `M${x0 + 46} ${roofY + 8} L${x1 - 46} ${roofY + 8} L${x1 - 34} ${shY - 8} L${x0 + 34} ${shY - 8} Z`;
  return (
    <Ghost>
      <path d={`${path} ${glass}`} fill="currentColor" fillRule="evenodd" opacity="0.85" />
      {/* taillights */}
      <rect x={x0 + 10} y={truck ? shY + 14 : shY + 4} width="36" height="10" rx="5" fill="none" stroke="currentColor" strokeWidth="3" />
      <rect x={x1 - 46} y={truck ? shY + 14 : shY + 4} width="36" height="10" rx="5" fill="none" stroke="currentColor" strokeWidth="3" />
      {/* license plate */}
      <rect x="140" y={truck ? shY + 32 : shY + 20} width="40" height="13" rx="3" fill="none" stroke="currentColor" strokeWidth="3" />
    </Ghost>
  );
};

const InteriorFrontSilhouette = () => (
  <Ghost>
    <g fill="currentColor" opacity="0.85">
      <path d="M70 110 C70 80 78 56 92 48 C100 42 110 44 114 52 C120 64 120 84 116 110 Z" />
      <path d="M150 110 C150 80 158 56 172 48 C180 42 190 44 194 52 C200 64 200 84 196 110 Z" />
    </g>
    <circle cx="252" cy="56" r="24" fill="none" stroke="currentColor" strokeWidth="5" />
    <path d="M226 92 L290 92 L290 110" fill="none" stroke="currentColor" strokeWidth="4" opacity="0.6" />
  </Ghost>
);

const InteriorRearSilhouette = () => (
  <Ghost>
    <path
      d="M50 110 L50 64 C50 54 58 48 68 48 L252 48 C262 48 270 54 270 64 L270 110 Z M116 56 L116 110 L110 110 L110 56 Z M204 56 L204 110 L198 110 L198 56 Z"
      fill="currentColor" fillRule="evenodd" opacity="0.85"
    />
    <path d="M50 86 L270 86" fill="none" stroke="currentColor" strokeWidth="3" opacity="0.4" />
  </Ghost>
);

const ProblemSilhouette = () => (
  <Ghost>
    <circle cx="160" cy="62" r="44" fill="none" stroke="currentColor" strokeWidth="5" strokeDasharray="10 8" />
    <path d="M160 38 L160 70 M160 80 L160 86" fill="none" stroke="currentColor" strokeWidth="6" strokeLinecap="round" />
  </Ghost>
);

// ── Photo steps ─────────────────────────────────────────────
const STEPS = [
  { id: "driver_side", label: "Driver side", instruction: "Step back 5-10 feet and line your vehicle up with the outline, bumper to bumper.", Silhouette: ({ bodyType }) => <SideSilhouette bodyType={bodyType} />, required: true },
  { id: "passenger_side", label: "Passenger side", instruction: "Same shot from the other side — full vehicle, bumper to bumper.", Silhouette: ({ bodyType }) => <SideSilhouette bodyType={bodyType} flip />, required: true },
  { id: "front", label: "Front", instruction: "Face the front of the vehicle. Include the hood, bumper, and windshield.", Silhouette: FrontSilhouette, required: true },
  { id: "rear", label: "Rear", instruction: "Face the back of the vehicle. Include the tailgate or trunk and rear bumper.", Silhouette: RearSilhouette, required: true },
  { id: "interior_front", label: "Front seats", instruction: "Open the driver door. Capture both front seats, the dash, and the floor mats.", Silhouette: InteriorFrontSilhouette, required: true },
  { id: "interior_rear", label: "Back seats", instruction: "Open a rear door. Capture the back seats and rear floor.", Silhouette: InteriorRearSilhouette, required: true },
  { id: "problem_areas", label: "Problem areas", instruction: "Optional — get close-ups of anything you want us to see: stains, pet hair, scratches, wheels.", Silhouette: ProblemSilhouette, required: false, multi: true },
];

const requiredCount = STEPS.filter((s) => s.required).length;

// ── Scoped CSS ─────────────────────────────────────────────
const css = `
  .gpc-root { min-height:100vh; background:var(--bg); display:flex; justify-content:center; }
  .gpc-shell { width:100%; max-width:28rem; min-height:100vh; background:var(--card); display:flex; flex-direction:column; }

  .gpc-svg-fill { width:100%; height:100%; }

  .gpc-screen { padding:40px 24px 32px; display:flex; flex-direction:column; flex:1; }
  .gpc-screen-center { text-align:center; align-items:center; }

  .gpc-icon-badge { width:64px; height:64px; border-radius:16px; background:var(--blue-glow); display:flex; align-items:center; justify-content:center; color:var(--blue); margin-bottom:24px; }
  .gpc-icon-circle { width:64px; height:64px; border-radius:9999px; background:var(--blue-glow); display:flex; align-items:center; justify-content:center; color:var(--blue); margin-bottom:24px; }

  .gpc-title { font-family:var(--head); font-size:24px; font-weight:800; color:var(--text); margin:0 0 8px; letter-spacing:.5px; }
  .gpc-vehicle-label { color:var(--blue); font-size:13px; font-weight:600; margin-bottom:12px; }
  .gpc-subtitle { color:var(--muted); font-size:14px; line-height:1.55; margin:0 0 28px; max-width:20rem; }
  .gpc-screen-center .gpc-subtitle { margin-left:auto; margin-right:auto; }

  .gpc-label { color:#cbd5e1; font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:1.5px; margin:0 0 8px; }
  .gpc-select { width:100%; background:var(--card2); border:1px solid var(--border); color:var(--text); border-radius:12px; padding:14px 16px; font-size:14px; font-family:var(--body); appearance:none; margin-bottom:20px; outline:none; }
  .gpc-select:focus { border-color:var(--blue); }

  .gpc-body-grid { display:grid; grid-template-columns:repeat(2,1fr); gap:10px; margin-bottom:20px; }
  .gpc-body-btn { border-radius:12px; border:1px solid var(--border); background:var(--card2); color:#cbd5e1; padding:12px; font-size:14px; font-weight:500; cursor:pointer; transition:border-color .2s, background .2s, color .2s; }
  .gpc-body-btn:hover { border-color:#334155; }
  .gpc-body-btn.selected { border-color:var(--blue); background:rgba(59,130,246,.1); color:#93b5ff; }

  .gpc-preview-card { border-radius:16px; background:rgba(26,32,48,.6); border:1px solid rgba(31,42,56,.6); padding:14px 16px 12px; margin-bottom:24px; }
  .gpc-preview-card p { margin:0; }
  .gpc-preview-caption { color:var(--muted); font-size:11px; margin-bottom:4px; }
  .gpc-preview-art { color:rgba(59,130,246,.7); height:96px; }
  .gpc-preview-label { color:#cbd5e1; font-size:11px; text-align:center; font-weight:500; margin-top:4px; }

  .gpc-cta { width:100%; font-weight:700; border-radius:12px; padding:14px; font-size:14px; cursor:pointer; transition:background .2s; border:none; font-family:var(--body); display:flex; align-items:center; justify-content:center; gap:8px; }
  .gpc-cta-primary { background:var(--blue); color:#fff; box-shadow:0 0 20px var(--blue-glow); }
  .gpc-cta-primary:hover { filter:brightness(1.05); }
  .gpc-cta-primary:disabled { background:var(--card2); color:#475569; cursor:not-allowed; box-shadow:none; }
  .gpc-cta-secondary { background:var(--card2); color:#cbd5e1; }
  .gpc-cta-secondary:hover { background:#202738; }
  .gpc-cta-ghost { background:transparent; color:var(--muted); }
  .gpc-cta-ghost:hover { color:#cbd5e1; }
  .gpc-cta-pill { background:#e2e8f4; color:var(--bg); }
  .gpc-cta-pill:hover { background:#fff; }

  .gpc-link-back { background:transparent; border:none; color:#64748b; font-size:12px; cursor:pointer; margin-top:16px; transition:color .2s; }
  .gpc-link-back:hover { color:#cbd5e1; }

  .gpc-intro-list { width:100%; display:flex; flex-direction:column; gap:10px; margin-bottom:32px; }
  .gpc-intro-row { display:flex; align-items:center; gap:12px; background:rgba(26,32,48,.6); border-radius:12px; padding:12px 16px; }
  .gpc-intro-num { width:24px; height:24px; border-radius:9999px; background:#263244; color:#cbd5e1; font-size:11px; font-weight:700; display:flex; align-items:center; justify-content:center; flex-shrink:0; }
  .gpc-intro-text { color:#e2e8f4; font-size:14px; }

  .gpc-header { padding:20px 20px 16px; }
  .gpc-header-row { display:flex; align-items:center; gap:12px; margin-bottom:16px; }
  .gpc-header-back { background:transparent; border:none; padding:6px; margin-left:-6px; border-radius:8px; color:var(--muted); cursor:pointer; transition:background .2s; }
  .gpc-header-back:hover { background:var(--card2); }
  .gpc-header-info { flex:1; }
  .gpc-header-title { color:var(--text); font-size:15px; font-weight:600; margin:0; line-height:1.2; }
  .gpc-header-sub { color:#64748b; font-size:11px; margin-top:2px; }
  .gpc-progress { height:4px; background:var(--card2); border-radius:9999px; overflow:hidden; }
  .gpc-progress-fill { height:100%; background:var(--blue); border-radius:9999px; transition:width .3s; }

  .gpc-photo-screen { padding:0 20px 24px; display:flex; flex-direction:column; flex:1; }

  .gpc-capture-box { position:relative; border-radius:16px; background:rgba(26,32,48,.8); border:2px dashed #334155; aspect-ratio:4/3; margin-bottom:16px; overflow:hidden; display:flex; align-items:center; justify-content:center; }
  .gpc-capture-video { position:absolute; inset:0; width:100%; height:100%; object-fit:cover; }
  .gpc-capture-overlay { position:absolute; inset:0; display:flex; align-items:center; justify-content:center; pointer-events:none; }
  .gpc-capture-overlay-inner { width:88%; color:#fff; filter:drop-shadow(0 0 6px rgba(0,0,0,.8)); opacity:.8; }
  .gpc-capture-hint-row { position:absolute; top:12px; left:0; right:0; display:flex; justify-content:center; pointer-events:none; }
  .gpc-capture-hint { background:rgba(8,11,16,.7); color:#fff; font-size:11px; font-weight:500; padding:6px 12px; border-radius:9999px; }
  .gpc-capture-photo { width:100%; height:100%; object-fit:cover; }
  .gpc-capture-empty { color:rgba(100,116,139,.85); padding:24px 32px; width:100%; height:100%; display:flex; align-items:center; justify-content:center; }
  .gpc-capture-check { position:absolute; top:12px; right:12px; background:var(--blue); border-radius:9999px; padding:6px; color:#fff; display:flex; }

  .gpc-thumb-strip { display:flex; gap:8px; margin-bottom:16px; overflow-x:auto; padding-bottom:4px; }
  .gpc-thumb { position:relative; flex-shrink:0; }
  .gpc-thumb img { width:64px; height:64px; border-radius:8px; object-fit:cover; }
  .gpc-thumb-x { position:absolute; top:-6px; right:-6px; background:#263244; border:none; border-radius:9999px; padding:2px; color:#cbd5e1; cursor:pointer; display:flex; }

  .gpc-step-instruction { color:#cbd5e1; font-size:14px; line-height:1.55; margin:0 0 24px; }

  .gpc-actions { display:flex; flex-direction:column; gap:10px; margin-top:auto; }
  .gpc-actions-row { display:flex; gap:10px; }
  .gpc-actions-row .gpc-cta-primary { flex:1; }
  .gpc-actions-row .gpc-cta-secondary { width:auto; padding:14px 18px; }

  .gpc-error { color:rgba(251,191,36,.9); font-size:11px; text-align:center; margin:0; }

  .gpc-review-text { color:var(--muted); font-size:14px; margin:0 0 20px; }
  .gpc-review-grid { display:grid; grid-template-columns:repeat(2,1fr); gap:12px; margin-bottom:24px; }
  .gpc-review-tile { position:relative; border-radius:12px; overflow:hidden; aspect-ratio:4/3; background:var(--card2); border:none; padding:0; cursor:pointer; }
  .gpc-review-tile img { width:100%; height:100%; object-fit:cover; }
  .gpc-review-caption { position:absolute; left:0; right:0; bottom:0; background:linear-gradient(to top, rgba(8,11,16,.9), transparent); padding:24px 10px 8px 10px; }
  .gpc-review-caption span { color:#fff; font-size:11px; font-weight:500; }

  .gpc-submit-progress { margin-top:32px; width:100%; max-width:20rem; height:6px; background:var(--card2); border-radius:9999px; overflow:hidden; }
  .gpc-submit-progress-fill { height:100%; width:66%; background:var(--blue); border-radius:9999px; animation:gpc-pulse 1.6s ease-in-out infinite; }
  @keyframes gpc-pulse { 0%,100% { opacity:1; } 50% { opacity:.55; } }

  .gpc-sparkle-anim { animation:gpc-pulse 1.6s ease-in-out infinite; }
`;

// ── Component ──────────────────────────────────────────────
// Screens: "vehicle" → "intro" → photo steps (0..n) → review → submitted
export default function GuidedPhotoCapture({ onComplete }) {
  const [screen, setScreen] = useState("vehicle");
  const [make, setMake] = useState("");
  const [model, setModel] = useState("");
  const [manualBodyType, setManualBodyType] = useState("");
  const [stepIndex, setStepIndex] = useState(0);
  const [photos, setPhotos] = useState({});
  const fileInputRef = useRef(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [cameraOn, setCameraOn] = useState(false);
  const [cameraError, setCameraError] = useState(false);

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    setCameraOn(false);
  }, []);

  const startCamera = useCallback(async () => {
    setCameraError(false);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment", width: { ideal: 1920 }, height: { ideal: 1440 } },
        audio: false,
      });
      streamRef.current = stream;
      setCameraOn(true);
    } catch (err) {
      console.error("Camera unavailable:", err);
      setCameraError(true);
    }
  }, []);

  // Attach stream to video element once it renders
  useEffect(() => {
    if (cameraOn && videoRef.current && streamRef.current) {
      videoRef.current.srcObject = streamRef.current;
      videoRef.current.play().catch(() => {});
    }
  }, [cameraOn]);

  // Stop camera when leaving the photos screen or unmounting
  useEffect(() => {
    if (screen !== "photos") stopCamera();
    return stopCamera;
  }, [screen, stopCamera]);

  const capturePhoto = useCallback(() => {
    const video = videoRef.current;
    const s = STEPS[stepIndex];
    if (!video || !s) return;
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext("2d").drawImage(video, 0, 0);
    const dataUrl = canvas.toDataURL("image/jpeg", 0.9);
    setPhotos((prev) => {
      const existing = prev[s.id] || [];
      const entry = { id: `${s.id}_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`, dataUrl, file: null };
      return { ...prev, [s.id]: s.multi ? [...existing, entry] : [entry] };
    });
    if (!s.multi) stopCamera();
  }, [stepIndex, stopCamera]);


  const isOtherMake = make === "Other";
  const bodyType = isOtherMake ? manualBodyType : make && model ? VEHICLES[make]?.[model] : "";
  const vehicleLabel = isOtherMake
    ? BODY_TYPES.find((b) => b.id === manualBodyType)?.label || ""
    : make && model
    ? `${make} ${model}`
    : "";

  const step = STEPS[stepIndex];
  const capturedRequired = STEPS.filter((s) => s.required && photos[s.id]?.length).length;
  const totalPhotoCount = Object.values(photos).reduce((n, arr) => n + arr.length, 0);

  const handleFile = useCallback(
    (e) => {
      const files = Array.from(e.target.files || []);
      if (!files.length || !step) return;
      files.forEach((file) => {
        const reader = new FileReader();
        reader.onload = () => {
          setPhotos((prev) => {
            const existing = prev[step.id] || [];
            const entry = { id: `${step.id}_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`, dataUrl: reader.result, file };
            return { ...prev, [step.id]: step.multi ? [...existing, entry] : [entry] };
          });
        };
        reader.readAsDataURL(file);
      });
      e.target.value = "";
    },
    [step]
  );

  const removePhoto = (stepId, photoId) => {
    setPhotos((prev) => ({ ...prev, [stepId]: (prev[stepId] || []).filter((p) => p.id !== photoId) }));
  };

  const handleSubmit = () => {
    const tagged = STEPS.flatMap((s) => (photos[s.id] || []).map((p) => ({ position: s.id, label: s.label, dataUrl: p.dataUrl, file: p.file })));
    setScreen("submitted");
    if (onComplete) onComplete({ vehicle: { make: isOtherMake ? "Other" : make, model: isOtherMake ? "" : model, bodyType }, photos: tagged });
  };

  // ── Screen: vehicle selection ─────────────────────────────
  if (screen === "vehicle") {
    const models = make && !isOtherMake ? Object.keys(VEHICLES[make] || {}) : [];
    const canContinue = isOtherMake ? !!manualBodyType : !!(make && model);
    return (
      <Shell>
        <div className="gpc-screen">
          <div className="gpc-icon-badge"><CarIcon size={32} /></div>
          <h1 className="gpc-title">What do you drive?</h1>
          <p className="gpc-subtitle">We'll customize your photo guide to match your vehicle's shape.</p>

          <p className="gpc-label">Make</p>
          <select
            value={make}
            onChange={(e) => { setMake(e.target.value); setModel(""); setManualBodyType(""); }}
            className="gpc-select"
          >
            <option value="">Select make…</option>
            {Object.keys(VEHICLES).map((m) => (
              <option key={m} value={m}>{m}</option>
            ))}
            <option value="Other">Other / not listed</option>
          </select>

          {make && !isOtherMake && (
            <>
              <p className="gpc-label">Model</p>
              <select
                value={model}
                onChange={(e) => setModel(e.target.value)}
                className="gpc-select"
              >
                <option value="">Select model…</option>
                {models.map((m) => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
            </>
          )}

          {isOtherMake && (
            <>
              <p className="gpc-label">Vehicle type</p>
              <div className="gpc-body-grid">
                {BODY_TYPES.map((b) => (
                  <button
                    key={b.id}
                    onClick={() => setManualBodyType(b.id)}
                    className={`gpc-body-btn ${manualBodyType === b.id ? "selected" : ""}`}
                  >
                    {b.label}
                  </button>
                ))}
              </div>
            </>
          )}

          {bodyType && (
            <div className="gpc-preview-card">
              <p className="gpc-preview-caption">Your photo guide</p>
              <div className="gpc-preview-art">
                <SideSilhouette bodyType={bodyType} />
              </div>
              <p className="gpc-preview-label">{vehicleLabel}</p>
            </div>
          )}

          <button
            onClick={() => canContinue && setScreen("intro")}
            disabled={!canContinue}
            className="gpc-cta gpc-cta-primary"
            style={{ marginTop:"auto" }}
          >
            Continue
          </button>
        </div>
      </Shell>
    );
  }

  // ── Screen: intro ─────────────────────────────────────────
  if (screen === "intro") {
    return (
      <Shell>
        <div className="gpc-screen gpc-screen-center">
          <div className="gpc-icon-badge"><CameraIcon size={32} /></div>
          <h1 className="gpc-title">Get your instant quote</h1>
          <p className="gpc-vehicle-label">{vehicleLabel}</p>
          <p className="gpc-subtitle">Take {requiredCount} quick photos. Our AI reviews them and builds your quote — no waiting for a callback.</p>
          <div className="gpc-intro-list">
            {STEPS.filter((s) => s.required).map((s, i) => (
              <div key={s.id} className="gpc-intro-row">
                <span className="gpc-intro-num">{i + 1}</span>
                <span className="gpc-intro-text">{s.label}</span>
              </div>
            ))}
          </div>
          <button onClick={() => { setStepIndex(0); setScreen("photos"); }} className="gpc-cta gpc-cta-primary">
            Start photos
          </button>
          <button onClick={() => setScreen("vehicle")} className="gpc-link-back">
            Change vehicle
          </button>
        </div>
      </Shell>
    );
  }

  // ── Screen: submitted ─────────────────────────────────────
  if (screen === "submitted") {
    return (
      <Shell>
        <div className="gpc-screen gpc-screen-center" style={{ paddingTop:64, paddingBottom:40 }}>
          <div className="gpc-icon-circle"><span className="gpc-sparkle-anim"><SparklesIcon size={32} /></span></div>
          <h2 className="gpc-title" style={{ fontSize:20 }}>Building your quote</h2>
          <p className="gpc-subtitle">We're analyzing {totalPhotoCount} photos of your {vehicleLabel} now. Your quote will be ready in under a minute.</p>
          <div className="gpc-submit-progress">
            <div className="gpc-submit-progress-fill" />
          </div>
        </div>
      </Shell>
    );
  }

  // ── Screen: review ────────────────────────────────────────
  if (screen === "review") {
    return (
      <Shell>
        <Header title="Review your photos" onBack={() => { setStepIndex(STEPS.length - 1); setScreen("photos"); }} progress={1} />
        <div className="gpc-photo-screen">
          <p className="gpc-review-text">Make sure each shot is clear and well-lit. Tap any photo to retake it.</p>
          <div className="gpc-review-grid">
            {STEPS.map((s) =>
              (photos[s.id] || []).map((p) => (
                <button
                  key={p.id}
                  onClick={() => { setStepIndex(STEPS.findIndex((x) => x.id === s.id)); setScreen("photos"); }}
                  className="gpc-review-tile"
                >
                  <img src={p.dataUrl} alt={s.label} />
                  <div className="gpc-review-caption">
                    <span>{s.label}</span>
                  </div>
                </button>
              ))
            )}
          </div>
          <button onClick={handleSubmit} className="gpc-cta gpc-cta-primary">
            <SparklesIcon size={16} />
            Generate my quote
          </button>
        </div>
      </Shell>
    );
  }

  // ── Screen: photo capture steps ───────────────────────────
  const stepPhotos = photos[step.id] || [];
  const hasPhoto = stepPhotos.length > 0;
  const Silhouette = step.Silhouette;

  const goNext = () => {
    if (stepIndex < STEPS.length - 1) setStepIndex(stepIndex + 1);
    else setScreen("review");
  };
  const goBack = () => {
    if (stepIndex > 0) setStepIndex(stepIndex - 1);
    else setScreen("intro");
  };

  return (
    <Shell>
      <Header
        title={step.label}
        onBack={goBack}
        progress={(stepIndex + 1) / (STEPS.length + 1)}
        stepLabel={`${Math.min(stepIndex + 1, requiredCount)} of ${requiredCount}${step.required ? "" : " · optional"} · ${vehicleLabel}`}
      />

      <div className="gpc-photo-screen">
        <div className="gpc-capture-box">
          {cameraOn ? (
            <>
              <video
                ref={videoRef}
                playsInline
                muted
                autoPlay
                className="gpc-capture-video"
              />
              <div className="gpc-capture-overlay">
                <div className="gpc-capture-overlay-inner">
                  <Silhouette bodyType={bodyType} />
                </div>
              </div>
              <div className="gpc-capture-hint-row">
                <span className="gpc-capture-hint">
                  Step back 5–10 ft · line your vehicle up with the outline
                </span>
              </div>
            </>
          ) : hasPhoto && !step.multi ? (
            <img src={stepPhotos[0].dataUrl} alt={step.label} className="gpc-capture-photo" />
          ) : (
            <div className="gpc-capture-empty">
              <Silhouette bodyType={bodyType} />
            </div>
          )}
          {!cameraOn && hasPhoto && !step.multi && (
            <div className="gpc-capture-check">
              <CheckIcon size={16} />
            </div>
          )}
        </div>

        {step.multi && stepPhotos.length > 0 && (
          <div className="gpc-thumb-strip">
            {stepPhotos.map((p) => (
              <div key={p.id} className="gpc-thumb">
                <img src={p.dataUrl} alt="Problem area" />
                <button
                  onClick={() => removePhoto(step.id, p.id)}
                  className="gpc-thumb-x"
                  aria-label="Remove photo"
                >
                  <XIcon size={12} />
                </button>
              </div>
            ))}
          </div>
        )}

        <p className="gpc-step-instruction">{step.instruction}</p>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          multiple={step.multi}
          onChange={handleFile}
          style={{ display:"none" }}
        />

        <div className="gpc-actions">
          {cameraOn ? (
            <div className="gpc-actions-row">
              <button
                onClick={stopCamera}
                className="gpc-cta gpc-cta-secondary"
              >
                Cancel
              </button>
              <button
                onClick={capturePhoto}
                className="gpc-cta gpc-cta-primary"
              >
                <CameraIcon size={16} />
                Capture
              </button>
            </div>
          ) : !hasPhoto || step.multi ? (
            <>
              <button onClick={startCamera} className="gpc-cta gpc-cta-primary">
                {step.multi ? <PlusIcon size={16} /> : <CameraIcon size={16} />}
                {step.multi ? (stepPhotos.length ? "Add another photo" : "Open camera") : "Open camera"}
              </button>
              {cameraError && (
                <p className="gpc-error">
                  Couldn't access the camera — you can upload a photo instead.
                </p>
              )}
              <button
                onClick={() => fileInputRef.current?.click()}
                className="gpc-cta gpc-cta-secondary"
              >
                Upload from library
              </button>
            </>
          ) : (
            <button
              onClick={() => { setPhotos((prev) => ({ ...prev, [step.id]: [] })); startCamera(); }}
              className="gpc-cta gpc-cta-secondary"
            >
              <RotateCcwIcon size={16} />
              Retake
            </button>
          )}

          {!cameraOn && (hasPhoto || !step.required) && (
            <button
              onClick={goNext}
              className={`gpc-cta ${hasPhoto ? "gpc-cta-pill" : "gpc-cta-ghost"}`}
            >
              {hasPhoto ? (
                <>
                  Looks good — next
                  <CheckIcon size={16} />
                </>
              ) : (
                "Skip this step"
              )}
            </button>
          )}
        </div>
      </div>
    </Shell>
  );
}

// ── Layout helpers ─────────────────────────────────────────

function Shell({ children }) {
  return (
    <div className="gpc-root">
      <style>{css}</style>
      <div className="gpc-shell">{children}</div>
    </div>
  );
}

function Header({ title, onBack, progress, stepLabel }) {
  return (
    <div className="gpc-header">
      <div className="gpc-header-row">
        <button onClick={onBack} className="gpc-header-back" aria-label="Go back">
          <ChevronLeftIcon size={20} />
        </button>
        <div className="gpc-header-info">
          <h2 className="gpc-header-title">{title}</h2>
          {stepLabel && <p className="gpc-header-sub">{stepLabel}</p>}
        </div>
      </div>
      <div className="gpc-progress">
        <div className="gpc-progress-fill" style={{ width: `${Math.round(progress * 100)}%` }} />
      </div>
    </div>
  );
}
